import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Calculator, TrendingUp, TrendingDown, AlertTriangle, Play } from 'lucide-react';
import { toast } from 'sonner';
import { quantSimulate, getErrorMessage } from '../../lib/api';
import EquityChart from './EquityChart';
import GlossaryTerm from './GlossaryTerm';

const fmtMoney = (v) => v == null ? '—' : `€ ${v.toLocaleString('it-IT', {minimumFractionDigits: 0, maximumFractionDigits: 0})}`;
const fmtPct = (v) => v == null ? '—' : `${v > 0 ? '+' : ''}${(v * 100).toFixed(2)}%`;
const fmtNum = (v, d = 2) => v == null ? '—' : v.toFixed(d);
const ROI_TONE = (roi) => roi > 0 ? 'text-emerald-600' : 'text-red-500';

/**
 * Simulatore di investimento: "se avessi investito X€ in strategia Y su Z…"
 *
 * Props:
 *  - strategyName: nome strategia (richiesto)
 *  - symbol: ticker (richiesto)
 *  - assetMeta: dict da universe (per mostrare nome esteso)
 *  - defaultStart, defaultEnd: date suggerite
 */
const InvestmentSimulator = ({ strategyName, symbol, assetMeta, defaultStart, defaultEnd }) => {
  const [capital, setCapital] = useState(10000);
  const [start, setStart] = useState(defaultStart || '2010-01-01');
  const [end, setEnd] = useState(defaultEnd || new Date().toISOString().slice(0, 10));
  const [compound, setCompound] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const handleRun = async () => {
    if (!strategyName || !symbol) {
      toast.error('Seleziona prima una strategia e un asset');
      return;
    }
    setRunning(true);
    try {
      const r = await quantSimulate({
        strategy: strategyName,
        symbol,
        initial_capital: capital,
        start, end, compound,
      });
      setResult(r.data);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Simulazione fallita'));
    } finally {
      setRunning(false);
    }
  };

  const profit = result ? result.final_equity - result.initial_capital : null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Calculator className="w-4 h-4 text-primary" />
          Simulatore d'investimento
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Se avessi messo questi soldi in <span className="font-mono">{strategyName}</span> su{' '}
          <span className="font-mono">{symbol}{assetMeta?.name ? ` · ${assetMeta.name}` : ''}</span>{' '}
          nel periodo scelto, ti saresti ritrovato con… <em>quanto?</em>
        </p>
      </CardHeader>

      <CardContent>
        {/* Form parametri */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <div className="col-span-2 md:col-span-1">
            <Label className="text-xs">Capitale (€)</Label>
            <Input
              type="number" min="100" step="500"
              value={capital}
              onChange={e => setCapital(parseFloat(e.target.value) || 0)}
              className="h-9"
            />
          </div>
          <div>
            <Label className="text-xs">Dal</Label>
            <Input type="date" value={start} onChange={e => setStart(e.target.value)} className="h-9" />
          </div>
          <div>
            <Label className="text-xs">Al</Label>
            <Input type="date" value={end} onChange={e => setEnd(e.target.value)} className="h-9" />
          </div>
          <div className="flex items-end gap-2 pb-1">
            <div className="flex flex-col gap-1">
              <Label className="text-xs cursor-pointer">
                <GlossaryTerm term="equal_weight_compound">Compound</GlossaryTerm>
              </Label>
              <Switch checked={compound} onCheckedChange={setCompound} />
            </div>
          </div>
          <div className="flex items-end">
            <Button onClick={handleRun} disabled={running} className="w-full h-9">
              <Play className="w-3.5 h-3.5 mr-1" />
              {running ? 'Calcolo…' : 'Simula'}
            </Button>
          </div>
        </div>

        {/* Hint */}
        {!result && !running && (
          <div className="rounded-md border border-dashed bg-muted/20 p-4 text-xs text-muted-foreground">
            💡 <strong>Cosa fa</strong>: ricostruisce il backtest della strategia esattamente come avrebbe operato nel passato — entry, exit, stop loss applicati alle stesse condizioni storiche — e calcola quanto sarebbero diventati i tuoi soldi alla fine.
            <br />
            <span className="block mt-1">
              ⚠️ <strong>Importante</strong>: risultati passati NON garantiscono risultati futuri. Questo è uno strumento educativo, non un consiglio finanziario.
            </span>
          </div>
        )}
        {running && <div className="h-32 bg-muted animate-pulse rounded-lg" />}

        {/* Risultato */}
        {result && !running && (
          <>
            <div className="rounded-lg border bg-gradient-to-br from-primary/5 to-transparent p-4 mb-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                Risultato simulazione
              </p>
              <p className="text-3xl font-bold tabular-nums">
                {fmtMoney(result.final_equity)}
              </p>
              <p className={`text-sm mt-1 ${ROI_TONE(result.roi_pct)}`}>
                {result.roi_pct >= 0 ? <TrendingUp className="w-3 h-3 inline" /> : <TrendingDown className="w-3 h-3 inline" />}
                {' '}
                {fmtPct(result.roi_pct)} totale
                <span className="text-muted-foreground ml-2">
                  (da {fmtMoney(result.initial_capital)} · profitto {fmtMoney(profit)})
                </span>
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                📅 {result.start} → {result.end} · {result.n_years.toFixed(1)} anni
                {result.compound_used && <Badge variant="outline" className="ml-2 text-[9px]">compound</Badge>}
              </p>
            </div>

            {/* KPI grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4 text-xs">
              <div className="rounded-md bg-muted/30 px-3 py-2">
                <p className="text-[10px] uppercase text-muted-foreground">
                  <GlossaryTerm term="cagr">CAGR</GlossaryTerm>
                </p>
                <p className={`font-bold tabular-nums ${ROI_TONE(result.cagr)}`}>{fmtPct(result.cagr)}</p>
                <p className="text-[10px] text-muted-foreground">tasso annuo</p>
              </div>
              <div className="rounded-md bg-muted/30 px-3 py-2">
                <p className="text-[10px] uppercase text-muted-foreground">
                  <GlossaryTerm term="sharpe">Sharpe</GlossaryTerm>
                </p>
                <p className="font-bold tabular-nums">{fmtNum(result.sharpe)}</p>
                <p className="text-[10px] text-muted-foreground">{result.sharpe >= 1 ? 'edge buono' : result.sharpe >= 0.5 ? 'edge debole' : 'no edge'}</p>
              </div>
              <div className="rounded-md bg-muted/30 px-3 py-2">
                <p className="text-[10px] uppercase text-muted-foreground">
                  <GlossaryTerm term="max_dd">Max DD</GlossaryTerm>
                </p>
                <p className="font-bold tabular-nums text-red-600">{fmtPct(result.max_dd)}</p>
                <p className="text-[10px] text-muted-foreground">peggior calo</p>
              </div>
              <div className="rounded-md bg-muted/30 px-3 py-2">
                <p className="text-[10px] uppercase text-muted-foreground">
                  <GlossaryTerm term="n_trades">Trade</GlossaryTerm>
                </p>
                <p className="font-bold tabular-nums">{result.n_trades}</p>
                <p className="text-[10px] text-muted-foreground">eseguiti</p>
              </div>
            </div>

            {/* Warning Max DD significativo */}
            {result.max_dd < -0.30 && (
              <div className="rounded-md border border-amber-500/40 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 mb-4 text-xs flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-700 dark:text-amber-300">Drawdown elevato durante il periodo</p>
                  <p className="text-amber-700/80 dark:text-amber-300/80 mt-0.5">
                    Per arrivare al risultato finale, la strategia ha attraversato un calo del {fmtPct(result.max_dd)}.
                    Significa che a un certo punto il tuo capitale è sceso di {fmtMoney(result.initial_capital * Math.abs(result.max_dd))}.
                    Avresti tenuto i nervi saldi senza vendere in panico?
                  </p>
                </div>
              </div>
            )}

            {/* Equity chart */}
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Come è cresciuto</p>
            <EquityChart
              data={{
                equity_curve: result.equity_curve.map(p => ({...p, value: (p.value / result.initial_capital) * 100})),
                benchmark_curve: result.benchmark_curve?.map(p => ({...p, value: (p.value / result.initial_capital) * 100})),
              }}
              mode="equity"
              showVolume={false}
              height={300}
            />
            <p className="text-[10px] text-muted-foreground mt-1 italic">
              Equity normalizzata a base 100 (= il tuo capitale iniziale). Linea blu = strategia, linea grigia tratteggiata = buy & hold sull'asset.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default InvestmentSimulator;
